import { useLayerStore } from '@/store/use-layer-store';
import { computed, ref, toRaw } from 'vue';
import { IDBPDatabase, openDB } from 'idb';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { cloneDeep, debounce } from 'lodash-es';
import { emitter } from '@/utils/emitter.utils';
import { EventType } from '@/utils/emitter.utils';
import { useEditorStore } from '@/store/use-editor-store';
import { Description } from '@/types/watchface';

interface Snapshot {
  themes: StoreThemeItem[];
  description: Description;
}

/**
 * 不同表盘对应的操作历史记录
 */
const historyMap = ref<
  Record<
    string,
    {
      index: number;
      max: number;
      min: 0;
    }
  >
>({});

let _dbCached: IDBPDatabase | undefined = undefined;

export const useSnapshot = () => {
  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();
  const editorStore = useEditorStore();

  const _STORE_NAME = 'snapshot-store';
  const STORE_SNAPSHOT = 'snapshots';
  const STORE_FILE = 'files';

  const _getDb = async () => {
    const db =
      _dbCached ??
      (await openDB(_STORE_NAME, 1, {
        upgrade(db) {
          // 保存snapshots
          db.createObjectStore(STORE_SNAPSHOT);
          // 保存文件
          db.createObjectStore(STORE_FILE);
        }
      }));
    _dbCached = db;
    return db;
  };

  const _saveSnapshot = async (index: number | string, value: Snapshot) => {
    const db = await _getDb();
    await db.put(STORE_SNAPSHOT, value, index);
  };

  const _getSnapshotKey = (index: number) => {
    const _id = watchfaceStore._id;
    return _id + ' index:' + index;
  };

  /**
   * 记录一个snapshot，返回对应的index
   */
  const record = async () => {
    const _id = watchfaceStore._id;

    const _historyMap = historyMap.value;
    if (_historyMap[_id] == undefined)
      _historyMap[_id] = { index: -1, max: 0, min: 0 };

    try {
      // 将layer images等数据保存在indexedDB中
      // 保存layer数据
      const themes = layerStore.$state.themes;
      const description = watchfaceStore.description;

      // save snapshot
      const snapshot: Snapshot = {
        themes: cloneDeep(themes),
        description: toRaw(description)
      };

      // 新的index
      const index = ++_historyMap[_id].index;
      _historyMap[_id].max = index;

      const snapshotKey = _getSnapshotKey(index);
      await _saveSnapshot(snapshotKey, snapshot);
    } catch (error) {
      console.log(error);

      // 发生错误后，都重置为0，防止数据错乱
      _historyMap[_id] = { index: 0, max: 0, min: 0 };
    }

    historyMap.value = _historyMap;
    return _historyMap;
  };

  /**
   * 获取当前表盘的操作数据
   */
  const history = computed(() => {
    const _id = watchfaceStore._id;
    return historyMap.value[_id];
  });

  /**
   * 清空当前表盘历史数据
   */
  const clear = async () => {
    const _id = watchfaceStore._id;
    const items = historyMap.value;
    delete items[_id];
    historyMap.value = items;

    try {
      const _db = await _getDb();
      const keys = await _db
        .getAllKeys(STORE_SNAPSHOT)
        .then((keys) => keys.filter((key) => (key + '').includes(_id)));

      for (const key of keys) {
        // 然后清空snapshots
        await _db.delete(STORE_SNAPSHOT, key);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const _restoring = ref(false);
  /**
   * 将数据恢复到targetIndex，然后将当前的index设置为targetIndex
   * @param targetIndex
   */
  const _restore = async (targetIndex: number) => {
    const _id = watchfaceStore._id;
    const _history = historyMap.value[_id];
    // 没有历史数据，无法恢复
    if (!_history) return;
    // 目标index超过最大值，无法恢复
    if (_history.max < targetIndex) return;
    // 目标index小于最小值，无法恢复
    if (_history.min > targetIndex) return;
    // 无需回复
    if (targetIndex == _history.index) return;

    // 开始恢复数据
    if (_restoring.value) return;
    try {
      _restoring.value = true;
      // 获取保存的snapshot
      const _key = _getSnapshotKey(targetIndex);
      const _db = await _getDb();
      const snapshot: Snapshot = await _db.get(STORE_SNAPSHOT, _key);

      // 获取layer
      // const layerKey = snapshot.layer;
      // const layerStrig = await _db.get(STORE_FILE, layerKey);
      const themes = snapshot.themes;
      if (!themes) throw 'themes文件损坏，无法恢复';

      // 恢复description数据
      const description = snapshot.description;
      if (!description) throw 'description文件损坏，无法恢复';

      await layerStore.sync(themes);

      // description中仅允许处理imageFormat和imageCompression
      const desc = {
        ...watchfaceStore.description,
        imageFormat: description.imageFormat,
        imageCompression: description.imageCompression
      };
      await watchfaceStore.sync(desc);
      // 同步index
      _history.index = targetIndex;
    } catch (error) {
      console.log(error);
    } finally {
      _restoring.value = false;
      emitter.emit(EventType['snapshot:restore']);
    }

    // 验证themeId
    const themeId = editorStore.themeId;
    if (!layerStore.themes.some((item) => item.id == themeId)) {
      editorStore.selectTheme();
    }
  };

  // 撤销，返回上一步
  const undo = async () => {
    await _restore(history.value.index - 1);
  };

  // 恢复，回到下一步
  const redo = async () => {
    await _restore(history.value.index + 1);
  };

  return {
    record: debounce(record, 50),
    history: history,
    clear: clear,
    undo,
    redo
    // replacePath
  };
};
